import { Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, CircularProgress, InputAdornment, IconButton, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, List, ListItem, ListItemButton, ListItemText, ListItemIcon, MenuItem, Paper, Select, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography, Tabs, Tab } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { Add, AddComment, Analytics, Cancel, Comment, ContentCut, Delete, Edit, ExpandMore, Expand, FileDownload, LegendToggle, QueryStats, Save, Settings, VerticalAlignBottom } from "@mui/icons-material";
import { useTheme } from "@mui/styles";
import { useDispatch } from "react-redux";
import { useSocket } from "../../context/useSocket";
import { setStageData, setIsLoadingData, toggleStageStepdownLock } from "../../reducers/rtfoSlice";
import { updateUser } from "../../reducers/userSlice";
import { useSnackbar } from "notistack";
import axios from "axios";

import { RTFOEditStepdown } from "./RTFOEditStepdown";
import { RTFOEditComment } from "./RTFOEditComment";
import { RTFOStepdownDataTable } from "./RTFOStepdownDataTable";
import { RTFOEventsDetailsDataTable } from "./RTFOEventsDetailsDataTable";
import { RTFOEventsSummaryDataTable } from "./RTFOEventsSummaryDataTable";
import { RTFOStepdownChart } from "./RTFOStepdownChart";
import { RTFOStepdownSummaryTable } from "./RTFOStepdownSummaryTable";
import { RTFOFlushStepdownChart } from "./RTFOFlushStepdownChart";
import { RTFOPressureChartMemo, getTraceColor } from "./RTFOPressureChart";
import { RTFOFrictionModelChart } from "./RTFOFrictionModelChart";
import { RTFOFrictionModelDataTable } from "./RTFOFrictionModelDataTable";
import { RTFOND2StepdownChartMemo } from "./RTFOND2StepdownChart";
import { RTFOPumpdown } from "./RTFOPumpdown";
import { RTFOND2StepdownDataTable } from "./RTFOND2StepdownDataTable";
import { rtfoConversionFactors } from "./rtfoConversionFactors";

export const RTFOStepdown = ({ currentRTFO, darkMode, gridView, user, chartTemplates, setChartTemplates, selectedTemplate, setSelectedTemplate, defaultChartTemplate }) => {
  const ENV = process.env.REACT_APP_NODE_ENV;
  const BASE_URL_SSD = ENV === 'production' ? process.env.REACT_APP_BASE_URL_SSD : (ENV === 'staging' || ENV === 'development' ) ? process.env.REACT_APP_BASE_URL_SSD_STAGING : 'http://localhost:8080/api';

  const theme = useTheme();
  const dispatch = useDispatch();
  const { socket } = useSocket();
  const { enqueueSnackbar } = useSnackbar();
  const userEmail = user?.value?.email;

  const stageData = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
  const stepdownParams = stageData?.stepdownParams;
  const stepdownData = stageData?.stepdownData;
  const preSandStepdownData = stageData?.preSandStepdownData;
  const stageNumber = Number(String(stageData?.stageNumber)?.replace(/\D+/g, ''));

  const units = currentRTFO?.settings?.units || 'metric';

  const [tabItem, setTabItem] = useState(0);
  const [chartOverlaysPumping, setChartOverlaysPumping] = useState(['spikelines', 'legend', 'comments']);
  const [chartOverlaysStepdown, setChartOverlaysStepdown] = useState(['spikelines', 'legend']);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCommentMode, setIsCommentMode] = useState(false);
  const [editMode, setEditMode] = useState('breakdown');
  const [editStepdownVisible, setEditStepdownVisible] = useState(false);
  const [events, setEvents] = useState(stageData?.events || {});
  const [stageComments, setStageComments] = useState(stageData?.comments || []);
  const [eventsDataTableType, setEventsDataTableType] = useState(0);
  const [breakdownTimestamp, setBreakdownTimestamp] = useState(stageData?.metrics?.breakdownTimestamp || null);
  const [breakdownPressure, setBreakdownPressure] = useState(stageData?.metrics?.breakdownPressure || 0);
  const [preSandTimestamp, setPreSandTimestamp] = useState(stageData?.metrics?.preSandTimestamp);
  const [preSandPressure, setPreSandPressure] = useState(stageData?.metrics?.preSandPressure);
  const [preSandRate, setPreSandRate] = useState(stageData?.metrics?.preSandRate);
  const [selectedComment, setSelectedComment] = useState(null);
  const [editCommentVisible, setEditCommentVisible] = useState(false);
  const [frictionModelParams, setFrictionModelParams] = useState({
    fcCasing1: (stepdownParams?.fcCasing1 || 0.033) * (units === 'field' ? rtfoConversionFactors.fc(stepdownParams?.plExpCasing1 || 1.7) : 1), 
    plExpCasing1: stepdownParams?.plExpCasing1 || 1.7, 
    fcCasing2: (stepdownParams?.fcCasing2 || 0.085) * (units === 'field' ? rtfoConversionFactors.fc(stepdownParams?.plExpCasing2 || 1.701) : 1), 
    plExpCasing2: stepdownParams?.plExpCasing2 || 1.701
  });
  const [historicStages, setHistoricStages] = useState([]);
  const [selectedStages, setSelectedStages] = useState([]);
  const [averageCalculationsRange, setAverageCalculationsRange] = useState(() => {
    if (stageData?.metrics?.averageCalculationsRange?.[0] > 0 && stageData?.metrics?.averageCalculationsRange?.[1] > 0) {
      return stageData?.metrics?.averageCalculationsRange;
    } else {
      const startTimestamp = stageData?.metrics?.preSandTimestamp || stageData?.coldboreData?.[0]?.timestamp || null;
      const endTimestamp = stageData?.coldboreData?.findLast(d => d.proppant > 5 && d.rate > 4 && d.pressure > 30)?.timestamp || stageData?.coldboreData?.slice(-1)?.[0]?.timestamp;
      return [startTimestamp, endTimestamp];
    };
  });
  const [averagePumpingPressure, setAveragePumpingPressure] = useState(stageData?.metrics?.averagePumpingPressure);
  const [averagePumpingRate, setAveragePumpingRate] = useState(stageData?.metrics?.averagePumpingRate);
  const [dhGaugeShift, setDHGaugeShift] = useState({start: null, end: null});
  const [stageTrim, setStageTrim] = useState({start: stageData?.coldboreData?.[0]?.timestamp, end: stageData?.coldboreData?.slice(-1)?.[0]?.timestamp});
  const [editChartTemplatesVisible, setEditChartTemplatesVisible] = useState(false);
  const [PpipeArray, setPpipeArray] = useState([]);
  const [PpipeCoefficients, setPpipeCoefficients] = useState([]);
  const [pumpdownData, setPumpdownData] = useState([]);
  const isLoadingPumpdownData = useRef(false);
  const [preSandISIP, setPreSandISIP] = useState((stageData?.metrics?.preSandISIP || stageData?.metrics?.isip || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
  const [isISIPEditMode, setIsISIPEditMode] = useState(false);

  const sdtType = tabItem === 0 ? 'flush' : tabItem === 1 ? 'preSand' : tabItem === 2 ? 'pumpdown' : 'stageSummary';

  function handleChartOverlaysPumpingChange(e, newVal) {
    setChartOverlaysPumping(newVal);
  };
  function handleChartOverlaysStepdownChange(e, newVal) {
    setChartOverlaysStepdown(newVal);
  };
  function handleEditStepdownChange(e, newVal) {
    setEditStepdownVisible(newVal);
  };
  function handleEditModeChange(e, newVal) {
    if (newVal) {
      setEditMode(newVal);
      setIsEditMode(true);
      setIsCommentMode(false);
    } else {
      setIsEditMode(false);
      setIsCommentMode(false);
      setEditMode(null);
    };
  };

  // Update events and comments
  useEffect(() => {
    setEvents(stageData?.events || {});
    setStageComments(stageData?.comments || []);
    setDHGaugeShift({start: null, end: null});
    setStageTrim({start: stageData?.coldboreData?.[0]?.timestamp, end: stageData?.coldboreData?.slice(-1)?.[0]?.timestamp});

    const stepdownParams = stageData?.stepdownParams;
    setFrictionModelParams({
      fcCasing1: (stepdownParams?.fcCasing1 || 0.033) * (units === 'field' ? rtfoConversionFactors.fc(stepdownParams?.plExpCasing1 || 1.7) : 1), 
      plExpCasing1: stepdownParams?.plExpCasing1 || 1.7, 
      fcCasing2: (stepdownParams?.fcCasing2 || 0.085) * (units === 'field' ? rtfoConversionFactors.fc(stepdownParams?.plExpCasing2 || 1.701) : 1), 
      plExpCasing2: stepdownParams?.plExpCasing2 || 1.701
    });
  }, [stageData, units]);

  // Update presand timestamp
  useEffect(() => {
    setPreSandTimestamp(stageData?.metrics?.preSandTimestamp);
  }, [stageData?.metrics?.preSandTimestamp]);

  // Update breakdown timestamp and pressure
  useEffect(() => {
    setBreakdownTimestamp(stageData?.metrics?.breakdownTimestamp || null);
    setBreakdownPressure(stageData?.metrics?.breakdownPressure || 0);
  }, [stageData?.metrics?.breakdownTimestamp, stageData?.metrics?.breakdownPressure]);

  // Update average P and Q calculations range
  useEffect(() => {
    let averageCalculationsRange = [];
    if (stageData?.metrics?.averageCalculationsRange?.[0] > 0 && stageData?.metrics?.averageCalculationsRange?.[1] > 0) {
      averageCalculationsRange = stageData?.metrics?.averageCalculationsRange;
    } else {
      const startTimestamp = stageData?.metrics?.preSandTimestamp || stageData?.coldboreData?.[0]?.timestamp || null;
      const endTimestamp = stageData?.coldboreData?.findLast(d => d.proppant > 5 && d.rate > 4 && d.pressure > 30)?.timestamp || stageData?.coldboreData?.slice(-1)?.[0]?.timestamp;

      averageCalculationsRange = [startTimestamp, endTimestamp];
    };
    const pumpingData = stageData?.coldboreData?.filter(d => d.timestamp >= averageCalculationsRange?.[0] && d.timestamp <= averageCalculationsRange?.[1]);
    const averagePumpingPressure = (pumpingData?.reduce((tot, curr) => tot + curr.pressure, 0) / pumpingData?.length) || 0;
    const averagePumpingRate = (pumpingData?.reduce((tot, curr) => tot + curr.rate, 0) / pumpingData?.length) || 0;
    setAveragePumpingPressure(averagePumpingPressure);
    setAveragePumpingRate(averagePumpingRate);
    setAverageCalculationsRange(averageCalculationsRange);
  }, [stageData?.metrics?.averageCalculationsRange, stageData?.metrics?.preSandTimestamp, stageData?.coldboreData]);

  // Update average P and Q calculations
  useEffect(() => {
    const pumpingData = stageData?.coldboreData?.filter(d => d.timestamp >= averageCalculationsRange?.[0] && d.timestamp <= averageCalculationsRange?.[1]);
    const averagePumpingPressure = (pumpingData?.reduce((tot, curr) => tot + curr.pressure, 0) / pumpingData?.length) || 0;
    const averagePumpingRate = (pumpingData?.reduce((tot, curr) => tot + curr.rate, 0) / pumpingData?.length) || 0;
    setAveragePumpingPressure(averagePumpingPressure);
    setAveragePumpingRate(averagePumpingRate);
  }, [averageCalculationsRange, stageData?.coldboreData]);

  // Update pumpdown data
  useEffect(() => {
    setPumpdownData(null);

    isLoadingPumpdownData.current = true;
    const timestamp = new Date().getTime();
    
    axios.get(`https://ssd-pumpdowndata.s3.us-east-2.amazonaws.com/${currentRTFO?.currentStageName}_pumpdown_data.json?timestamp=${timestamp}`)
    .then(res => {
      setPumpdownData(res.data);
      isLoadingPumpdownData.current = false;
    })
    .catch(err => {
      isLoadingPumpdownData.current = false;
    })
  }, [currentRTFO?.currentStageName]);

  const handleStagesChange = (event) => {
    const { target: { value } } = event;

    if (typeof value !== 'string' && value?.indexOf('Deselect All') !== -1) {
      setSelectedStages([]);
    } else if (typeof value !== 'string' && value?.indexOf('Select All') !== -1) {
      const stagesWithData = currentRTFO?.stageData ? Object.keys(currentRTFO?.stageData) : [];
      const currentStageIndex = stagesWithData?.indexOf(currentRTFO?.currentStageName);
      const stageNames = currentStageIndex >= 0 ? stagesWithData?.slice(0, currentStageIndex) : [];
      setSelectedStages(stageNames);
    } else {
      setSelectedStages(
        // On autofill we get a stringified value.
        typeof value === 'string' ? value.split(',') : value,
      );
    };
  };

  useEffect(() => {
    const stagesWithData = currentRTFO?.stages || [];
    // const stagesWithData = currentRTFO?.stageData ? Object.keys(currentRTFO?.stageData) : [];
    const currentStageIndex = stagesWithData?.indexOf(currentRTFO?.currentStageName);
    const stageNames = currentStageIndex >= 0 ? stagesWithData?.slice(0, currentStageIndex) : [];
    setHistoricStages(stageNames);
  }, [currentRTFO?.currentStageName, currentRTFO?.stages]);

  useEffect(() => {
    setSelectedStages(historicStages);
  }, [historicStages]);

  // Shift DH gauge data
  useEffect(() => {

    if (dhGaugeShift?.start > 0 && dhGaugeShift?.end > 0) {

      const delta = parseInt((dhGaugeShift?.end - dhGaugeShift?.start) / 1000);

      const newColdboreData = shiftDHGaugeData(stageData?.coldboreData, delta);

      dispatch(setStageData({stageData: {...stageData, coldboreData: newColdboreData}, initialLoad: false, highResolutionData: true, liveMonitoring: false, incrementalData: false}));

      setDHGaugeShift({start: null, end: null});

    };

    function shiftDHGaugeData(array, delta) {
      // Create a copy of the original array to avoid modifying the original data
      const shiftedArray = JSON.parse(JSON.stringify(array));
    
      // Iterate through the array and shift dh gauge values based on 'delta'
      for (let i = 0; i < shiftedArray.length; i++) {
        const shiftedIndex = i + delta;
        
        if (shiftedArray[i]) {
          shiftedArray[i].dhPressureTop = array[shiftedIndex]?.dhPressureTop || null;
          shiftedArray[i].dhPressureBottom = array[shiftedIndex]?.dhPressureBottom || null;
          shiftedArray[i].dhTemperatureTop = array[shiftedIndex]?.dhTemperatureTop || null;
          shiftedArray[i].dhTemperatureBottom = array[shiftedIndex]?.dhTemperatureBottom || null;
        };

      };
    
      return shiftedArray;
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dhGaugeShift]);

  // Update pre sand ISIP when stage changes
  useEffect(() => {
    setPreSandISIP((stageData?.metrics?.preSandISIP || stageData?.metrics?.isip || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
    // setPreSandISIP((parseFloat(stageData?.metrics?.preSandISIP) || parseFloat(stageData?.metrics?.isip) || 0)?.toFixed(2));
  }, [stageData, units]);

  // useCallback to memoize the handleDataUpdate function
  const handlePpipeFrictionFitUpdate = useCallback((newData) => {
    setPpipeCoefficients(newData);
  }, [setPpipeCoefficients]);

  const stepdownStatus =  stepdownData && stepdownData?.length > 0 && stepdownParams && Object.keys(stepdownParams)?.length > 0 ? 'available' :
                          currentRTFO?.isLoadingData ? 'loading' :
                          stageData?.metrics?.isip > 0 && (!stepdownParams || Object.keys(stepdownParams)?.length === 0) ? 'not-available' : 'awaiting';


  return (
    <Paper 
      elevation={0} 
      sx={{boxShadow: 1}} 
      style={{margin: '10px 20px 20px 20px', padding: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', backgroundColor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]}}
      tabIndex="2"
      onKeyDown={handleKeyDown}
    >
      <div style={{width: 'calc(100% - 20px)', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', margin: '20px 20px 0px 20px'}}>
        <Typography variant="h6">
          Stage Analysis
        </Typography>
      </div>

      <div style={{
        width: '100%', 
        display: 'flex', 
        flexDirection: 'row', 
        justifyContent: 'space-between',
        alignItems: 'center',
        // backgroundColor: (darkMode ? theme?.palette?.elevationOne?.dark : theme?.palette?.elevationTwo?.light),
        borderBottom: `1px solid ${darkMode ? 'grey' : 'lightgrey'}`,
        marginBottom: 10
      }}>
        <Tabs
          value={tabItem}
          textColor={darkMode ? "inherit" : "primary"}
          TabIndicatorProps={{style: {background: (darkMode ? '#fff' : theme?.palette?.primary?.main)}}}
          onChange={(e, value) => setTabItem(value)}
          style={{
            height: 24
          }}
        >
          <Tab label={<Typography fontSize="12px">Flush SDT</Typography>} key='flush' />
          <Tab label={<Typography fontSize="12px">Pre Sand SDT</Typography>} key='preSand' />
          <Tab label={<Typography fontSize="12px">Pumpdown</Typography>} key='pumpdown' disabled={!pumpdownData || pumpdownData?.length === 0} />
          <Tab label={<Typography fontSize="12px">Stage Summary (BETA)</Typography>} key='stageSummary' disabled={!pumpdownData || pumpdownData?.length === 0} />
        </Tabs>


        <FormControlLabel
          control={<Switch />} 
          label={<Typography style={{fontSize: 12}}>Lock Stage</Typography>}
          size="small" 
          checked={stageData?.stepdownLocked ? true : false} 
          onChange={() => {
            if (socket && socket.readyState === 1) {
              socket?.send(JSON.stringify({
                messageType: 'saveStage',
                stage: {...stageData, stepdownLocked: stageData?.stepdownLocked ? false : true}
              }));
            };
            dispatch(toggleStageStepdownLock({ stageName: stageData?.name }));
          }}
        />
      </div>

      {(sdtType !== 'pumpdown' && sdtType !== 'stageSummary') && <div style={{width: '100%', display: 'flex', marginBottom: 4, flexDirection: gridView ? 'row' : 'column', justifyContent: 'space-between'}}>
        <div 
          style={{
            width: gridView ? '50%' : '100%',
            height: gridView ? 580 : 900,
            padding: '40px 0px 0px 0px', 
            // margin: 5,
            position: 'relative', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            outline: ((isEditMode && '5px solid #f44336') || (isCommentMode && '5px solid green')),
            zIndex: editMode?.length > 0 && 1000
        }}>
     
          <div 
            style={{
              zIndex: 69, 
              position: 'absolute',
              width: 200,
              left: '50%', 
              top: -35, 
              transform: 'translateX(-50%)', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              backgroundColor: isEditMode ? '#f44336' : isCommentMode ? 'green' : '',
              borderRadius: '10px 10px 0 0'
            }}
          >
            {isEditMode && editMode !== 'trim' ? <Typography variant="h6" color="#fff">EDIT MODE</Typography> : 
            (isEditMode && editMode === 'trim') ? <Typography variant="h6" color="#fff">TRIMMING MODE</Typography> : 
            isCommentMode ? <Typography variant="h6" color="#fff">COMMENT MODE</Typography> : ''}
          </div>
          {(isEditMode || isCommentMode) && <div 
            style={{
              zIndex: 69, 
              position: 'absolute', 
              left: '50%', 
              bottom: 0, 
              transform: 'translateX(-50%)', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              width: '100%'
            }}
          >
            {
            isCommentMode ? <Typography variant="body2" color="green">Click to add or remove comments</Typography> :
            editMode === 'perfClosure' ? <Typography variant="body2" color="#f44336">Click to add or remove rate drop points</Typography> : 
            editMode === 'breakdown' ? <Typography variant="body2" color="#f44336">Click to set fracture breakdown line</Typography> :
            editMode === 'preSand' ? <Typography variant="body2" color="#f44336">Click to set pre sand line</Typography> :
            editMode === 'plugSlip' ? <Typography variant="body2" color="#f44336">Click to set plug slip event</Typography> :
            editMode === 'sandoff' ? <Typography variant="body2" color="#f44336">Click to set sandoff event</Typography> :
            editMode === 'isolationIssue' ? <Typography variant="body2" color="#f44336">Click to set isolation issue event</Typography> :
            editMode === 'equipmentIssue' ? <Typography variant="body2" color="#f44336">Click to set equipment issue event</Typography> :
            editMode === 'lostSuction' ? <Typography variant="body2" color="#f44336">Click to set lost suction event</Typography> :
            editMode === 'other' ? <Typography variant="body2" color="#f44336">Click to set other event</Typography> :
            editMode === 'fracfeed' ? <Typography variant="body2" color="#f44336">Click to set fracfeed event</Typography> :
            editMode === 'ballSeating' ? <Typography variant="body2" color="#f44336">Click to set ball seating event</Typography> :
            editMode === 'start' ? <Typography variant="body2" color="#f44336">Click to set start of average pressure and rate calculations</Typography> :
            editMode === 'end' ? <Typography variant="body2" color="#f44336">Click to set end of average pressure and rate calculations</Typography> :
            editMode === 'dhGauge' ? <Typography variant="body2" color="#f44336">Click once to set target point, click again to shift DH gauge data</Typography> : 
            editMode === 'trim' ? <Typography variant="body2" color="#f44336">Click to set start and end of stage data</Typography> :  
            ''}
          </div>}
            
          <div style={{display: 'flex', position: 'absolute', top: 10, left: 10, zIndex: 69}}>
            <ToggleButtonGroup
              value={chartOverlaysPumping}
              size="small"
              style={{height: 24}}
              onChange={handleChartOverlaysPumpingChange}
            >
              <ToggleButton value="spikelines" size="small" title="Show Info on Hover" color={(darkMode ? "" : "primary")}>
                <QueryStats style={{fontSize: '16px'}} />
              </ToggleButton>
              <ToggleButton value="legend" size="small" title="Show Legend" color={(darkMode ? "" : "primary")}>
                <LegendToggle style={{fontSize: '16px'}} />
              </ToggleButton>
              <ToggleButton value="comments" size="small" title="Show Comments" color={(darkMode ? "" : "primary")}>
                <Comment style={{fontSize: '16px'}} />
              </ToggleButton>
            </ToggleButtonGroup>
            
            <Divider flexItem orientation="vertical" style={{ margin: 5 }} />

            <ToggleButtonGroup
              value={editMode}
              size="small"
              style={{height: 24}}
              onChange={handleEditModeChange}
              exclusive
              disabled={stageData?.stepdownLocked}
            >
              <ToggleButton value="start" size="small" title="Edit Start of Average Pressure and Rate" color={(darkMode ? "" : "primary")}>
                <VerticalAlignBottom style={{transform: 'rotate(90deg)', fontSize: '16px'}} />
              </ToggleButton>
              <ToggleButton value="end" size="small" title="Edit End of Average Pressure and Rate" color={(darkMode ? "" : "primary")}>
                <VerticalAlignBottom style={{transform: 'rotate(-90deg)', fontSize: '16px'}} />
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider flexItem orientation="vertical" style={{ margin: 5 }} />

            <ToggleButtonGroup
              title="Enter Comment Mode"
              value={isCommentMode}
              size="small"
              style={{height: 24}}
              onChange={() => {
                if (isCommentMode) {
                  if (editMode === 'start' || editMode === 'end' || !editMode) setEditMode('breakdown');
                  setIsCommentMode(false);
                } else {
                  if (editMode === 'start' || editMode === 'end' || !editMode) setEditMode('breakdown');
                  setIsCommentMode(true);
                  setIsEditMode(false);
                };
              }}
              exclusive
              disabled={stageData?.stepdownLocked}
            >
              <ToggleButton value={true} size="small" title="Add a Comment" color={(darkMode ? "" : "primary")}>
                <AddComment style={{fontSize: '16px'}} />
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider flexItem orientation="vertical" style={{ margin: 5 }} />

            <ToggleButtonGroup
              value={editMode}
              size="small"
              style={{height: 24}}
              onChange={handleEditModeChange}
              exclusive
            >
              <ToggleButton value="dhGauge" size="small" title="Shift DH Gauge Data" color={(darkMode ? "" : "primary")}>
                <Expand style={{fontSize: '16px', transform: 'rotate(90deg)'}} />
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider flexItem orientation="vertical" style={{ margin: 5 }} />

            <ToggleButtonGroup
              value={editMode}
              size="small"
              style={{height: 24}}
              onChange={handleEditModeChange}
              exclusive
            >
              <ToggleButton value="trim" size="small" title="Trim Stage Data" color={(darkMode ? "" : "primary")}>
                <ContentCut style={{fontSize: '16px'}} />
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider flexItem orientation="vertical" style={{ margin: 5 }} />

            <ToggleButtonGroup
              title="Enter Events Edit Mode"
              value={isEditMode}
              size="small"
              style={{height: 24}}
              onChange={() => {
                if (isEditMode) {
                  if (editMode === 'start' || editMode === 'end' || !editMode) {
                    setEditMode('breakdown');
                  };
                  setIsEditMode(false);
                } else {
                  if (editMode === 'start' || editMode === 'end' || !editMode) {
                    setEditMode('breakdown');
                  };
                  setIsEditMode(true);
                  setIsCommentMode(false);
                };
              }}
              exclusive
              disabled={stageData?.stepdownLocked}
            >
              <ToggleButton value={true} size="small" title="Edit Events" color={(darkMode ? "" : "primary")}>
                <Edit style={{fontSize: '16px'}} />
              </ToggleButton>
            </ToggleButtonGroup>


            {isEditMode && editMode !== 'start' && editMode !== 'end' && editMode !== 'dhGauge' && editMode !== 'trim' && <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
              <Typography fontSize={'12px'} style={{marginLeft: 10}}>Event Type</Typography>
              <FormControl style={{marginLeft: 5}}>
                <Select 
                  value={editMode}
                  onChange={(e) => setEditMode(e.target.value)}
                  size="small"
                  style={{width: 175, height: 24, fontSize: '12px'}}
                >
                  <MenuItem value={'breakdown'}>Breakdown</MenuItem>
                  <MenuItem value={'preSand'}>Pre Sand</MenuItem>
                  <MenuItem value={'perfClosure'}>Rate Drop</MenuItem>
                  <MenuItem value={'plugSlip'}>Plug Slip</MenuItem>
                  <MenuItem value={'sandoff'}>Sandoff</MenuItem>
                  <MenuItem value={'isolationIssue'}>Isolation Issue</MenuItem>
                  <MenuItem value={'equipmentIssue'}>Equipment Issue</MenuItem>
                  <MenuItem value={'lostSuction'}>Lost Suction</MenuItem>
                  <MenuItem value={'other'}>Other Event</MenuItem>
                  <MenuItem value={'fracfeed'}>Fracfeed Event</MenuItem>
                  <MenuItem value={'ballSeating'}>Ball Seating</MenuItem>
                </Select>
              </FormControl>
            </div>}
          </div>

          {!isEditMode && 
            <ToggleButtonGroup
              value={''}
              size="small"
              style={{height: 24, position: 'absolute', top: 10, right: 10}}
              onChange={() => setEditChartTemplatesVisible(prev => !prev)}
            >
              <ToggleButton value={true} size="small" title="Chart Settings" color={(darkMode ? "" : "primary")} onClick={() => {}} >
                <Settings style={{fontSize: '16px'}} />
              </ToggleButton>
            </ToggleButtonGroup>
          }


          {!isEditMode && !isCommentMode && <FormControl style={{position: 'absolute', top: 10, right: 60}}>
            <Select 
              value={selectedTemplate}
              label="Chart template"
              onChange={(e) => setSelectedTemplate(e.target.value)}
              size="small"
              variant="standard"
              style={{width: 200, height: 28, zIndex: 69, fontSize: '12px'}}
            >
              {Object.keys(chartTemplates)?.map((template,i) => (
                <MenuItem key={`${template}-${i}`} value={template}>
                  <ListItemText primary={template} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>}


          {isEditMode && 
          <ToggleButtonGroup
            value={''}
            size="small"
            style={{height: 24, position: 'absolute', top: 10, right: 10}}
            onChange={() => {}}
          >
            <ToggleButton value={'1'} size="small" title="Cancel Changes" color={(darkMode ? "" : "primary")} onClick={handleCancelChanges} >
              <Cancel style={{fontSize: '16px'}} />
            </ToggleButton>
            <ToggleButton value={'2'} size="small" title="Save Changes" color={(darkMode ? "" : "primary")} onClick={handleSaveChanges}>
              <Save style={{fontSize: '16px'}} />
            </ToggleButton>
          </ToggleButtonGroup>}

          {stageNumber && stageData?.coldboreData ? 
            <RTFOPressureChartMemo
            // <RTFOPressureChart 
              gridView={gridView} 
              showSpikeLines={!isEditMode && !isCommentMode && chartOverlaysPumping.includes('spikelines')} 
              showLegend={chartOverlaysPumping.includes('legend')}
              showComments={chartOverlaysPumping.includes('comments')}
              editMode={editMode} 
              isEditMode={isEditMode}
              isCommentMode={isCommentMode}
              events={events}
              setEvents={setEvents}
              stageComments={stageComments} 
              setStageComments={setStageComments} 
              setEditCommentVisible={setEditCommentVisible}
              setSelectedComment={setSelectedComment}
              breakdownTimestamp={breakdownTimestamp}
              setBreakdownTimestamp={setBreakdownTimestamp}
              setBreakdownPressure={setBreakdownPressure}
              preSandTimestamp={preSandTimestamp}
              setPreSandTimestamp={setPreSandTimestamp}
              setPreSandPressure={setPreSandPressure}
              setPreSandRate={setPreSandRate}
              averageCalculationsRange={averageCalculationsRange}
              setAverageCalculationsRange={setAverageCalculationsRange}
              averagePumpingPressure={averagePumpingPressure}
              averagePumpingRate={averagePumpingRate}
              dhGaugeShift={dhGaugeShift}
              setDHGaugeShift={setDHGaugeShift}
              stageTrim={stageTrim}
              setStageTrim={setStageTrim}
              chartTemplate={chartTemplates?.[selectedTemplate]}
            /> 
          : 
          currentRTFO?.isLoadingData ? 
          <div>
            <CircularProgress
              size={24}
            />
          </div> :
          <div>
            <Typography variant="body2">No data selected</Typography>
          </div>}
        </div>

        <Divider orientation={gridView ? "vertical" : "horizontal"} flexItem style={{margin: gridView ? 0 : 20, zIndex: 999}} />

        <div style={{
            width: gridView ? '50%' : '100%',
            height: gridView ? 580 : 900,
            padding: '40px 0px 10px 0px', 
            marginTop: 0,
            position: 'relative', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center'
        }}>
          <div style={{display: 'flex', flexDirection: 'row', position: 'absolute', justifyContent: 'space-between', alignItems: 'center', width: '100%', top: 10, left: 10, zIndex: 69}}>
            <div style={{display: 'flex', flexDirection: 'row'}}>
              <ToggleButtonGroup
                value={chartOverlaysStepdown}
                size="small"
                style={{height: 24}}
                onChange={handleChartOverlaysStepdownChange}
              >
                <ToggleButton value="spikelines" size="small" title="Show Info on Hover" color={(darkMode ? "" : "primary")}>
                  <QueryStats style={{fontSize: '16px'}} />
                </ToggleButton>
                <ToggleButton value="legend" size="small" title="Show Legend" color={(darkMode ? "" : "primary")}>
                  <LegendToggle style={{fontSize: '16px'}} />
                </ToggleButton>
              </ToggleButtonGroup>
              <Divider flexItem orientation="vertical" style={{ margin: 5 }} />
              <ToggleButtonGroup
                value={editStepdownVisible}
                size="small"
                style={{ height: 24 }}
                onChange={handleEditStepdownChange}
                exclusive
                disabled={stageData?.stepdownLocked}
              >
                <ToggleButton value={true} size="small" title="Edit Stepdown" color={(darkMode ? "" : "primary")}>
                  <Edit style={{fontSize: '16px'}} />
                </ToggleButton>
              </ToggleButtonGroup>
            </div>

            {sdtType === 'preSand' && <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', marginRight: 20, marginTop: 0, height: 24}}>
              <Typography variant="caption">Pre Sand ISIP:</Typography>
              <TextField
                label="Pre Sand ISIP"
                value={preSandISIP}
                onChange={handleISIPChange}
                size="small"
                style={{marginRight: 10, marginLeft: 10}}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{units === 'field' ? 'psi' : 'MPa'}</InputAdornment>,
                  style: {height: 24, width: 150, fontSize: 14}
                }}
                onFocus={event => {
                  event.target.select();
                }}
                disabled={!isISIPEditMode}
              />
              {!isISIPEditMode && <IconButton onClick={() => setIsISIPEditMode(true)} size="small"><Edit /></IconButton>}
              {isISIPEditMode && <IconButton onClick={handleCancelISIP} size="small"><Cancel /></IconButton>}
              {isISIPEditMode && <IconButton onClick={handleSaveISIP} size="small" disabled={!isISIPEditMode} style={{marginLeft: 10}}><Save /></IconButton>}
            </div>}


          </div>
          {
          (sdtType !== 'pumpdown' && sdtType !== 'stageSummary') && stepdownStatus === 'available' ? 
          <RTFOStepdownChart
            stepdownData={stepdownData}
            gridView={gridView}
            currentRTFO={currentRTFO} 
            darkMode={darkMode}
            showSpikeLines={chartOverlaysStepdown?.includes('spikelines')} 
            showLegend={chartOverlaysStepdown?.includes('legend')}
            sdtType={sdtType}
            preSandStepdownData={preSandStepdownData}
          /> :
          stepdownStatus === 'loading' ? 
          <div>
            <CircularProgress
              size={24}
            />
          </div> :
          stepdownStatus === 'not-available' ?
          <div style={{
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute', 
            height: 200,
            width: 400,
            top: '50%', 
            left: '50%', 
            transform: 'translate(-200px, -100px)'
          }}>
            <Analytics style={{fontSize: '72px', color: 'grey'}} />
            <Typography variant="body2" style={{color: 'grey'}}>Stepdown not available</Typography>
          </div> :
          <div style={{
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute', 
            height: 200,
            width: 400,
            top: '50%', 
            left: '50%', 
            transform: 'translate(-200px, -100px)'
          }}>
            <CircularProgress size={40} style={{marginBottom: 20}} />
            <Typography variant="body2" style={{color: 'grey'}}>Awaiting stepdown data...</Typography>
          </div>
          }
        </div>  
      </div>}

      {(sdtType === 'pumpdown' || sdtType === 'stageSummary') && <RTFOPumpdown currentRTFO={currentRTFO} stageData={stageData} pumpdownData={pumpdownData} isLoadingPumpdownData={isLoadingPumpdownData} gridView={gridView} sdtType={sdtType} darkMode={darkMode} />}

      {(sdtType !== 'pumpdown' && sdtType !== 'stageSummary') && <Accordion defaultExpanded disableGutters elevation={0} style={{borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]}}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            SDT Results
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div style={{width: '100%', display: 'flex', justifyContent: 'center'}}>
            <div style={{width: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
              {stepdownStatus === 'available' ? 
              <RTFOStepdownSummaryTable 
                stepdownParams={{
                  ...stepdownParams, 
                  preSandDperf: Number((Number(stepdownParams?.preSandDperf))?.toFixed(2)), 
                  Dperf: Number((Number(stepdownParams?.Dperf))?.toFixed(2))
                }} 
                metrics={stageData?.metrics} 
                perfClosure={events?.perfClosure || []} 
                units={currentRTFO?.settings?.units || 'metric'}
                darkMode={darkMode}
              /> :
              <div style={{
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Analytics style={{fontSize: '72px', color: 'grey'}} />
                <Typography variant="body2" style={{color: 'grey'}}>Stepdown not available</Typography>
              </div>
              }
            </div>

            <Divider orientation="vertical" flexItem style={{marginTop: 4, marginBottom: -10, zIndex: 999}} />

            <div style={{
                width: '50%',
                height: 540, 
                padding: '20px 0', 
                position: 'relative', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              {
              stepdownStatus === 'available' ?
              <RTFOFlushStepdownChart 
              gridView={true}
              currentRTFO={currentRTFO} 
              darkMode={darkMode}
              sdtType={sdtType}
              /> :
              stepdownStatus === 'loading' ? 
              <div>
                <CircularProgress
                  size={24}
                />
              </div> :
              stepdownStatus === 'not-available' ?
              <div style={{
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute', 
                height: 200,
                width: 400,
                top: '50%', 
                left: '50%', 
                transform: 'translate(-200px, -100px)'
              }}>
                <Analytics style={{fontSize: '72px', color: 'grey'}} />
                <Typography variant="body2" style={{color: 'grey'}}>Stepdown not available</Typography>
              </div> :
              <div style={{
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute', 
                height: 200,
                width: 400,
                top: '50%', 
                left: '50%', 
                transform: 'translate(-200px, -100px)'
              }}>
                <CircularProgress size={40} style={{marginBottom: 20, color: 'grey'}} />
                <Typography variant="body2" style={{color: 'grey'}}>Awaiting stepdown data...</Typography>
              </div>
              }
            </div>  

          </div>
        </AccordionDetails>
      </Accordion>}
      
      {sdtType === 'flush' && <Accordion defaultExpanded disableGutters elevation={0} style={{borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]}}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            ND{<sup>2</sup>} Workflow
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
         <div style={{height: 540, marginBottom: 40, display: 'flex', flexDirection: 'row'}}> 
            <div style={{width: '50%', height: 540}}>
              <RTFOND2StepdownChartMemo
                gridView={true}
                sdtType={sdtType}
                darkMode={darkMode}
              />
            </div>
            <div style={{width: '50%', height: 540}}>
              <RTFOND2StepdownDataTable currentRTFO={currentRTFO} darkMode={darkMode} />
            </div>
          </div>
        </AccordionDetails>
      </Accordion>}
      
      {(sdtType !== 'pumpdown' && sdtType !== 'stageSummary') && <Accordion defaultExpanded disableGutters elevation={0} style={{borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]}}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            Stage Friction Model
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div style={{width: '100%', display: 'flex', justifyContent: 'center'}}>
            <div style={{width: '50%', height: 600, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
              {stepdownStatus === 'available' ? 
              <RTFOFrictionModelDataTable currentRTFO={currentRTFO} frictionModelParams={frictionModelParams} setFrictionModelParams={setFrictionModelParams} setPpipeArray={setPpipeArray} PpipeCoefficients={PpipeCoefficients} darkMode={darkMode}/> 
              :
              <div style={{
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Analytics style={{fontSize: '72px', color: 'grey'}} />
                <Typography variant="body2" style={{color: 'grey'}}>Stepdown not available</Typography>
              </div>
              }
            </div>
            <Divider orientation="vertical" flexItem style={{marginTop: 4, marginBottom: -10, zIndex: 999}} />

            <div style={{
              width: '50%',
              height: 540, 
              padding: '20px 0', 
              position: 'relative', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {
              stepdownStatus === 'available' ?
              <div style={{position: 'relative', height: '100%', width: '100%'}}>
                
                <FormControl style={{position: 'absolute', top: -20, left: 20}}>
                  <Select 
                    value={selectedStages}
                    label="Stages"
                    multiple
                    onChange={handleStagesChange}
                    size="small"
                    variant="standard"
                    style={{width: 200, height: 28, zIndex: 69, fontSize: '12px'}}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    <MenuItem value='Select All'>
                      <Checkbox checked={true} />
                      <ListItemText primary='(Select All)' />
                    </MenuItem>
                    <MenuItem value='Deselect All'>
                      <Checkbox checked={false} />
                      <ListItemText primary='(Deselect All)' />
                    </MenuItem>
                    {historicStages?.map((name,i) => (
                      <MenuItem key={`${name}-${i}`} value={name}>
                        <Checkbox checked={selectedStages.indexOf(name) > -1} />
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Button size="small" style={{height: 28, position: 'absolute', right: 0, top: -20, zIndex: 69, color: (darkMode ? '#fff' : theme.palette.primary.main)}} startIcon={<FileDownload />} onClick={handleDownloadChartData}>Download CSV</Button>

                <RTFOFrictionModelChart frictionModelParams={frictionModelParams} selectedStages={selectedStages} PpipeArray={PpipeArray} PpipeCoefficients={PpipeCoefficients} handlePpipeFrictionFitUpdate={handlePpipeFrictionFitUpdate} sdtType={sdtType} /> 


              </div> :
              stepdownStatus === 'loading' ?
              <div>
                <CircularProgress
                  size={24}
                />
              </div> :
              stepdownStatus === 'not-available' ?
              <div style={{
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute', 
                height: 200,
                width: 400,
                top: '50%', 
                left: '50%', 
                transform: 'translate(-200px, -100px)'
              }}>
                <Analytics style={{fontSize: '72px', color: 'grey'}} />
                <Typography variant="body2" style={{color: 'grey'}}>Stepdown not available</Typography>
              </div> :
              <div style={{
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute', 
                height: 200,
                width: 400,
                top: '50%', 
                left: '50%', 
                transform: 'translate(-200px, -100px)'
              }}>
                <CircularProgress size={40} style={{marginBottom: 20}} />
                <Typography variant="body2" style={{color: 'grey'}}>Awaiting stepdown data...</Typography>
              </div>
              }
            </div>  

          </div>
        </AccordionDetails>
      </Accordion>}

      {(sdtType !== 'pumpdown' && sdtType !== 'stageSummary') && <Accordion defaultExpanded disableGutters elevation={0} style={{borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]}}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            Pad {sdtType === 'flush' ? 'Flush' : 'Pre Sand'} Stepdown Summary
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div style={{ height: 700 }}>
            <RTFOStepdownDataTable currentRTFO={currentRTFO} sdtType={sdtType} darkMode={darkMode} />
          </div>
        </AccordionDetails>
      </Accordion>}

      {(sdtType !== 'pumpdown' && sdtType !== 'stageSummary') && <Accordion defaultExpanded disableGutters elevation={0} style={{borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]}}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            Pad Events Summary
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Tabs
            value={eventsDataTableType}
            textColor={darkMode ? "inherit" : "primary"}
            TabIndicatorProps={{style: {background: (darkMode ? '#fff' : theme?.palette?.primary?.main)}}}
            onChange={(e, value) => setEventsDataTableType(value)}
          >
            <Tab label={<Typography fontSize="12px">Details</Typography>} key='details' />
            <Tab label={<Typography fontSize="12px">Summary</Typography>} key='summary' />
          </Tabs>
          <div style={{ height: 700 }}>
            {eventsDataTableType === 0 ? <RTFOEventsDetailsDataTable currentRTFO={currentRTFO} darkMode={darkMode} /> : <RTFOEventsSummaryDataTable currentRTFO={currentRTFO} darkMode={darkMode} />}
          </div>
        </AccordionDetails>
      </Accordion>}


      {editStepdownVisible && <RTFOEditStepdown 
        currentRTFO={currentRTFO} 
        darkMode={darkMode} 
        setEditStepdownVisible={setEditStepdownVisible}
        spikes={events?.perfClosure}
        sdtType={sdtType}
        preSandISIP={parseFloat(preSandISIP) / (units === 'field' ? rtfoConversionFactors.pressure : 1)}
        userEmail={userEmail}
      />}


      {editCommentVisible && <RTFOEditComment 
        stageData={stageData}
        editMode={editMode}
        setEditMode={setEditMode}
        darkMode={darkMode} 
        editCommentVisible={editCommentVisible}
        setEditCommentVisible={setEditCommentVisible} 
        selectedComment={selectedComment} 
        setSelectedComment={setSelectedComment}
        stageComments={stageComments}
        setStageComments={setStageComments}
      />}

      {editChartTemplatesVisible && <EditChartTemplates 
        chartTemplates={chartTemplates}
        setChartTemplates={setChartTemplates}
        selectedTemplate={selectedTemplate} 
        setSelectedTemplate={setSelectedTemplate} 
        setEditChartTemplatesVisible={setEditChartTemplatesVisible} 
        theme={theme} 
        darkMode={darkMode}
        user={user}
        userChartTemplates={user?.value?.rtfoChartTemplates || defaultChartTemplate}
        defaultChartTemplate={defaultChartTemplate}
        apiUrl={BASE_URL_SSD}
        dispatch={dispatch}
      />}
    </Paper>
  );

  // FUNCTIONS

  function handleCancelChanges() {
    try {
      
      setIsEditMode(false);
      setEditMode('breakdown');
      setIsCommentMode(false);
      setEvents(stageData?.events || {});
      setStageComments(stageData?.comments || []);
      setBreakdownTimestamp(stageData?.metrics?.breakdownTimestamp || null);
      setBreakdownPressure(stageData?.metrics?.breakdownPressure || 0);
      setPreSandTimestamp(stageData?.metrics?.preSandTimestamp);
      setPreSandPressure(stageData?.metrics?.preSandPressure);
      setPreSandRate(stageData?.metrics?.preSandRate);
      const startTimestamp = stageData?.metrics?.preSandTimestamp || stageData?.coldboreData?.[0]?.timestamp || null;
      const endTimestamp = stageData?.coldboreData?.findLast(d => d.proppant > 5 && d.rate > 4 && d.pressure > 30)?.timestamp || stageData?.coldboreData?.slice(-1)?.[0]?.timestamp;
      setAverageCalculationsRange([startTimestamp, endTimestamp]);
      const pumpingData = stageData?.coldboreData?.filter(d => d.timestamp >= averageCalculationsRange?.[0] && d.timestamp <= averageCalculationsRange?.[1]);
      const averagePumpingPressure = (pumpingData?.reduce((tot, curr) => tot + curr.pressure, 0) / pumpingData?.length) || 0;
      const averagePumpingRate = (pumpingData?.reduce((tot, curr) => tot + curr.rate, 0) / pumpingData?.length) || 0;
      setAveragePumpingPressure(averagePumpingPressure);
      setAveragePumpingRate(averagePumpingRate);

      setDHGaugeShift({start: null, end: null});
      setStageTrim({start: stageData?.coldboreData?.[0]?.timestamp, end: stageData?.coldboreData?.slice(-1)?.[0]?.timestamp});

      socket?.send(JSON.stringify({
        messageType: 'getStageData',
        stageName: stageData?.name,
        userEmail: userEmail
      }));

    } catch (error) {
      console.log(error);
      return;
    };
  };
  function handleSaveChanges() {
    try {

      setIsEditMode(false);
      setEditMode('breakdown');
      setIsCommentMode(false);
      
      const newEvents = {
        preSand: events?.preSand || [],
        breakdown: events?.breakdown || [],
        perfClosure: events?.perfClosure || [],
        plugSlip: (events?.plugSlip || stageData?.plugSlips) || [],
        sandoff: events?.sandoff || [],
        isolationIssue: events?.isolationIssue || [],
        equipmentIssue: events?.equipmentIssue || [],
        lostSuction: events?.lostSuction || [],
        other: events?.other || [],
        fracfeed: events?.fracfeed || [],
        ballSeating: events?.ballSeating || []
      };

      if (preSandTimestamp !== stageData?.metrics?.preSandTimestamp && stageData?.stepdownData?.length > 0) {
        dispatch(setIsLoadingData(true));

        socket?.send(JSON.stringify({
          messageType: 'recalculateStepdown',
          padName: currentRTFO?.name,
          stage: {...stageData, events: newEvents, comments: stageComments, metrics: {...stageData?.metrics, breakdownTimestamp, breakdownPressure, preSandTimestamp, preSandPressure, preSandRate, averageCalculationsRange, averagePumpingPressure, averagePumpingRate}},
          perfSchedule: currentRTFO?.perfSchedule,
          surveys: currentRTFO?.surveys,
          params: stageData?.stepdownParams?.lockedParams,
          saveStage: true,
          sdtType,
          showSnackbar: true
        }));
        enqueueSnackbar(`Stage ${stageData?.name} saved successfully`, { variant: 'success' });
      
      } else if (editMode === 'trim' && stageTrim?.start > 0 && stageTrim?.end > 0) {

          socket?.send(JSON.stringify({
            messageType: 'trimStage',
            stageName: stageData?.name,
            stageTrim
          }));
  
      } else {
        socket?.send(JSON.stringify({
          messageType: 'saveStage',
          stage: {...stageData, events: newEvents, comments: stageComments, metrics: {...stageData?.metrics, breakdownTimestamp, breakdownPressure, preSandTimestamp, preSandPressure, preSandRate, averageCalculationsRange, averagePumpingPressure, averagePumpingRate}},
        }));
      };


    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleDownloadChartData() {
    try {

      if (!currentRTFO?.stageData || Object.keys(currentRTFO?.stageData)?.length === 0) return;

      console.log('Downloading pad SDT friction data CSV...');

      let headers = [
        'Well Name', 
        'Well UWI', 
        'Stage',
        'SDT Type',
        'Step',
        'Depth of Calc. (m)',
        'Rate (m3/min)',
        'Pressure (MPa)',
        'ISIP (MPa)',
        'ISIP BH (MPa)',
        'Total Friction (MPa)',
        'Friction Grad. (kPa/m)',
        'Knwb (MPa/m3/min)',
        'Bnwb',
        'Dperf (mm)',
        'Coeff.',
        'PL Exp.'
      ];

      let csv = "";
      csv += headers.join(",") + "\r\n";


      for (const stageName of currentRTFO?.stages) {

        const stepdownData = currentRTFO?.stageData?.[stageName]?.stepdownData;
        const preSandStepdownData = currentRTFO?.stageData?.[stageName]?.preSandStepdownData;
        const stepdownParams = currentRTFO?.stageData?.[stageName]?.stepdownParams;
        const metrics = currentRTFO?.stageData?.[stageName]?.metrics;

        if ((!stepdownData?.length > 1 && !preSandStepdownData?.length > 1) || !stepdownParams) continue;

        if (preSandStepdownData?.length > 1) {
          for (const i in preSandStepdownData) {
            const d = preSandStepdownData?.[i];
            let row = `${stageName?.split('_')?.[1]},${currentRTFO?.stageData?.[stageName]?.wellUwi},${stageName?.split('_')?.[2]},Pre Sand,${parseInt(i) + 1},${stepdownParams?.averageDepth},${d?.rate || 0},${d?.pressure || 0},${stepdownParams?.preSandISIP || 0},${stepdownParams?.preSandISIPBH || 0},${(d?.pressure - stepdownParams?.preSandISIP) || 0},${((d?.pressure - stepdownParams?.preSandISIP) * 1000 / stepdownParams?.averageDepth) || 0},${(stepdownParams?.preSandKNwb / 145.038 * 6.2898) || 0},${(stepdownParams?.preSandBNwb) || 0},${(stepdownParams?.preSandDperf * 25.4) || 0},${stepdownParams?.preSandFCFrictionModel || 0},${stepdownParams?.preSandPLExpFrictionModel || 0}`;
            row += "\r\n";
            csv += row;
          };
        };

        if (stepdownData?.length > 1) {
          for (const i in stepdownData) {
            const d = stepdownData?.[i];
            let row = `${stageName?.split('_')?.[1]},${currentRTFO?.stageData?.[stageName]?.wellUwi},${stageName?.split('_')?.[2]},Flush,${parseInt(i) + 1},${stepdownParams?.averageDepth},${d?.rate || 0},${d?.pressure || 0},${stepdownParams?.isip || 0},${metrics?.isipBH},${(d?.pressure - stepdownParams?.isip) || 0},${((d?.pressure - stepdownParams?.isip) * 1000 / stepdownParams?.averageDepth) || 0},${(stepdownParams?.kNwb / 145.038 * 6.2898) || 0},${(stepdownParams?.bNwb) || 0},${(stepdownParams?.Dperf * 25.4) || 0},${stepdownParams?.fcFrictionModel || 0},${stepdownParams?.plExpFrictionModel || 0}`;
            row += "\r\n";
            csv += row;
          };
        };
      };

      const hiddenElement = document.createElement('a');  
      hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv);  
      hiddenElement.target = '_blank';  
      hiddenElement.download = `${currentRTFO?.name}_friction_model_data_${new Date()?.toISOString()}.csv`;  
      hiddenElement.click();  

      return;

    } catch (error) {
      console.log(error);
    };
  };

  function handleKeyDown(event) {
    try {
      
      if (event.keyCode === 27) { // escape button
        handleCancelChanges();
      };
  
    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleCancelISIP() {
    setPreSandISIP((stageData?.metrics?.preSandISIP || stageData?.metrics?.isip || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
    // setPreSandISIP((parseFloat(stageData?.metrics?.preSandISIP) || parseFloat(stageData?.metrics?.isip) || 0)?.toFixed(2));
    setIsISIPEditMode(false);
  };

  function handleSaveISIP() {
    if (preSandISIP > 0) {
      socket?.send(JSON.stringify({
        messageType: 'saveStage',
        stage: {...stageData, metrics: {...stageData?.metrics, preSandISIP: parseFloat(preSandISIP) / (units === 'field' ? rtfoConversionFactors.pressure : 1)}},
      }));
    };
    // dispatch(setStageData({stageData: {...stageData, metrics: {...stageData?.metrics, preSandISIP}}, initialLoad: false, highResolutionData: true, liveMonitoring: false, incrementalData: false}));
    setIsISIPEditMode(false);
  };

  function handleISIPChange(e) {
    const value = e.target.value;
    setPreSandISIP(value);
  };
};

function EditChartTemplates({ chartTemplates, setChartTemplates, selectedTemplate, setSelectedTemplate, setEditChartTemplatesVisible, theme, darkMode, user, userChartTemplates, defaultChartTemplate, apiUrl, dispatch }) {
  
  
  const allTraces = [
    'Treating Pressure (MPa)', 
    'Tubing Pressure (MPa)', 
    'Slurry Rate (m3/min)', 
    'Propp. Surf. (kg/m3)', 
    'Propp. BH (kg/m3)', 
    'Cum. Propp. (ton)', 
    'Cum. Propp. BH (ton)', 
    'DFR Conc. (kg/m3)', 
    'LFR Conc. (L/m3)', 
    'MFR/Cluster (kg/s)',
    'Fracfeed', 
    'Fracfeed Slope',
    'TP Rate (L/min)',
    'BHP Top (MPa)',
    'BHP Bottom (MPa)',
    'BHT Top (degC)',
    'BHT Bottom (degC)',
    'Cross-Linker Conc. (L/m3)',
    'Gellant Conc. (L/m3)'
  ];

  // const [selectedTraces, setSelectedTraces] = useState(chartTemplates[selectedTemplate] || allTraces);

  const [templateName, setTemplateName] = useState(selectedTemplate);

  useEffect(() => {
    setTemplateName(selectedTemplate);
  }, [chartTemplates, selectedTemplate]);

  useEffect(() => {
    // setSelectedTraces(chartTemplates[selectedTemplate]);
    setTemplateName(selectedTemplate);
  }, [chartTemplates, selectedTemplate]);

  return (
    <Dialog 
      open={true} 
      onClose={() => setEditChartTemplatesVisible(false)}
      fullWidth={true}
      maxWidth="sm"
      PaperProps={{
        style: {
          backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
        },
      }}
      elevation={0}
    >
      <DialogTitle>Edit Chart Templates</DialogTitle>
      <DialogContent>
        
        <Typography>Templates</Typography>

        <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 40}}>
          <FormControl>
            <Select 
              value={selectedTemplate}
              onChange={event => setSelectedTemplate(event.target.value)}
              size="small"
              variant="outlined"
              style={{width: 300, fontSize: '12px'}}
            >
              {Object.keys(chartTemplates)?.map((template,i) => (
                <MenuItem key={`${template}-${i}`} value={template}>
                  <ListItemText primary={template} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <div>
            <Button startIcon={<Delete />} size="small" style={{marginRight: 20}} disabled={Object.keys(chartTemplates)?.length === 1} onClick={handleDeleteTemplateClick}>Delete</Button>
            <Button startIcon={<Add />} size="small" onClick={handleNewTemplateClick}>New</Button>
          </div>
        </div>
        
        <TextField
          margin="normal"
          label="Template Name"
          placeholder="Template Name"
          type="text"
          value={templateName}
          variant="outlined"
          fullWidth
          onChange={e => setTemplateName(e.target.value)}
        />

        <Typography style={{marginTop: 40}}>Traces</Typography>
        <div style={{width: '100%', height: '500px', border: '1px solid lightgrey', borderRadius: 5, overflow: 'scroll'}}>
          <List disablePadding>
            <ListItem key={`select-all`} value={'selectAll'} style={{margin: 0, padding: 0}}>
              <ListItemButton role={undefined} onClick={() => handleTraceCheck('selectAll')} dense>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={true}
                    tabIndex={-1}
                    disableRipple
                    style={{color: '0758b7'}}
                  />
                </ListItemIcon>
                <ListItemText primary={'Select All'} />
              </ListItemButton>
            </ListItem>

            <Divider />

            <ListItem key={`deselect-all`} value={'deselectAll'} style={{margin: 0, padding: 0}}>
              <ListItemButton role={undefined} onClick={() => handleTraceCheck('deselectAll')} dense>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={false}
                    tabIndex={-1}
                    disableRipple
                    style={{color: '0758b7'}}
                  />
                </ListItemIcon>
                <ListItemText primary={'Deselect All'} />
              </ListItemButton>
            </ListItem>

            <Divider />

            {allTraces?.map((name,i) => (
              <div key={`${name}-${i}`}>
                <ListItem value={name} style={{margin: 0, padding: 0}}>
                  <ListItemButton role={undefined} onClick={() => handleTraceCheck(name)} dense>
                  {/* <ListItemButton role={undefined} onClick={() => handleTraceCheck(name)} dense disabled={name === 'Treating Pressure (MPa)' || name === 'Slurry Rate (m3/min)'}> */}
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={chartTemplates[selectedTemplate]?.indexOf(name) > -1}
                        tabIndex={-1}
                        disableRipple
                        style={{color: getTraceColor(name)}}
                      />
                    </ListItemIcon>
                    <ListItemText primary={name} />
                  </ListItemButton>
                </ListItem>

                <Divider />
              </div>
            ))}
          </List>
        </div>
        
      </DialogContent>
      <DialogActions style={{display: 'flex', justifyContent: 'flex-end'}}>
        <Button onClick={() => {
          setChartTemplates(() => (userChartTemplates || defaultChartTemplate));
          setEditChartTemplatesVisible(false);
        }} color={darkMode ? "inherit" : "primary"} style={{marginRight: 10}}>
          Cancel
        </Button>
        <Button onClick={handleSaveTemplateClick} color={darkMode ? "inherit" : "primary"}
          style={{color: darkMode ? '#000' : '#fff'}} variant="contained"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>

  );

  function handleTraceCheck(trace) {
    try {

      if (trace === 'selectAll') {
        setChartTemplates(prev => ({...prev, [selectedTemplate]: allTraces}));
      } else if (trace === 'deselectAll') {
        setChartTemplates(prev => ({...prev, [selectedTemplate]: []}));
        // setChartTemplates(prev => ({...prev, [selectedTemplate]: ['Treating Pressure (MPa)', 'Slurry Rate (m3/min)']}));
      } else {
        let newSelectedTraces = [...chartTemplates[selectedTemplate]];
        
        if (newSelectedTraces?.indexOf(trace) > -1) {
          newSelectedTraces = newSelectedTraces.filter(t => t !== trace);
        } else {
          newSelectedTraces.push(trace);
        };
        
        setChartTemplates(prev => ({...prev, [selectedTemplate]: newSelectedTraces}));
      };
    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleNewTemplateClick() {
    try {
      const newTemplateName = `New template ${Object.keys(chartTemplates)?.length + 1}`;
      setChartTemplates(prev => ({...prev, [newTemplateName]: allTraces}));
      setSelectedTemplate(newTemplateName);
    } catch (error) {
      console.log(error);
      return;
    }
  };

  function handleDeleteTemplateClick() {
    try {

      const newAllTemplates = {...chartTemplates};
      delete newAllTemplates[selectedTemplate];
      setChartTemplates(newAllTemplates);
      setSelectedTemplate(Object.keys(newAllTemplates)?.slice(-1)?.[0]);
    } catch (error) {
      console.log(error);
      return;
    };
  };

  async function handleSaveTemplateClick() {
    try {

      const newAllTemplates = JSON.parse(JSON.stringify(chartTemplates));
      if (templateName !== selectedTemplate) {
        newAllTemplates[templateName] = [...newAllTemplates[selectedTemplate]];
        delete newAllTemplates[selectedTemplate];
        setChartTemplates(newAllTemplates);
        setSelectedTemplate(templateName);
      };

      setEditChartTemplatesVisible(false);

      console.log(`Updating RTFO metrics slots for user ${user?.value?.email}`);

      await fetch(`${apiUrl}/users/rtfocharttemplates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY
        },
        body: JSON.stringify({
          email: user?.value?.email,
          rtfocharttemplates: newAllTemplates
        })
      });

      dispatch(updateUser({...user?.value, rtfoChartTemplates: newAllTemplates}));
    } catch (error) {
      console.log(error);
      return;
    };
  };
};